/**
 * AuthContext — the single entry point for authentication in the app.
 *
 * This is a thin React context that exposes the `IAuthContext` interface.
 * The actual auth logic lives in the provider-specific hooks:
 *   - `useMsalAuth`   (Microsoft / Azure AD)
 *   - `useGoogleAuth`  (Google Workspace)
 *
 * The active provider is chosen in `App.tsx` which renders either
 * `MsalAuthProviderWrapper` or `GoogleAuthProviderWrapper`, each of which
 * calls the appropriate hook and feeds it into this context.
 */

import { createContext, type ReactNode, useContext } from "react";
import type { IAuthContext } from "./auth/types";

// Re-export the type so existing `import { useAuth } from "@/context/AuthContext"` works.
export type { IAuthContext } from "./auth/types";

const AuthContext = createContext<IAuthContext | null>(null);

/**
 * Wrap children with the auth context value.
 *
 * This is called by the provider-specific wrapper components in `App.tsx`.
 * It does NOT call any hooks itself — the value is pre-computed by the caller.
 */
export function AuthContextProvider({ children, value }: Readonly<{ children: ReactNode; value: IAuthContext }>) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to consume the current auth context.
 *
 * This is the only auth hook that downstream components should use.
 */
export function useAuth(): IAuthContext {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// ---------------------------------------------------------------------------
// Legacy re-export: the original AuthContext.tsx exported `AuthProvider`.
// We keep the name so that existing imports don't break — App.tsx will
// replace its usage.
// ---------------------------------------------------------------------------
export { AuthContextProvider as AuthProvider };
